/**
 * Resolves navigation targets from backoffice feature flags:
 * - `frontend.UXImprovements.Login.functionalities` — v3 login shell vs legacy.
 * - `frontend.UXImprovements.Dashboard.functionalities` — home at `/v3/dashboard` vs `/desktop`.
 * - Mis cuentas v3 (`/v3/accounts`): ver {@link isUxMyAccountsImprovementsEnabled} y
 *   {@link UX_IMPROVEMENTS_MY_ACCOUNTS_FUNCTIONALITIES_ID} en `util/v3ShellLayoutSync`.
 * - Detalle v3 (`/v3/accounts/:id`): ver {@link isUxMyAccountsImprovementsEnabled} y {@link getBankAccountDetailPath}.
 * - Histórico transferencias v3 (`/v3/transfer/historic`): ver {@link isUxTransfersImprovementsEnabled} y
 *   {@link getTransferHistoricPath}.
 * - Descarga histórico transferencias v3 (`/v3/transfer/download`): ver {@link getTransferDownloadPath}.
 * - Cursar transferencia v3 (`/v3/transfer/:type`): ver {@link getTransferExecutePath}.
 * - Confirmación transferencia v3 (`/v3/transfer/confirmation`): ver {@link getTransferConfirmationPath}.
 * - Ticket transferencia v3 (`/v3/transfer/ticket`): ver {@link getTransferTicketPath}.
 * - Generar CREDIN v3 (`/v3/credin`): ver {@link getRequestCredinPath}, {@link getGenerateCredinPath} y
 *   {@link getCredinTicketPath}.
 */
import * as config from "util/config";
import {
    isCredinV3Enabled,
    isUxDashboardImprovementsEnabled,
    isUxMyAccountsImprovementsEnabled,
    isTransferHistoricV3Enabled,
} from "util/v3ShellLayoutSync";

/** @type {"/v2/transfer/historic"} */
export const TRANSFER_HISTORIC_LEGACY_PATH = "/v2/transfer/historic";

/** @type {"/v3/transfer/historic"} */
export const TRANSFER_HISTORIC_V3_PATH = "/v3/transfer/historic";

/** @type {"/v2/transferDownload"} */
export const TRANSFER_DOWNLOAD_LEGACY_PATH = "/v2/transferDownload";

/** @type {"/v3/transfer/download"} */
export const TRANSFER_DOWNLOAD_V3_PATH = "/v3/transfer/download";

/** @type {"/v2/transfer"} */
export const TRANSFER_EXECUTE_V2_PREFIX = "/v2/transfer";

/** @type {"/v3/transfer"} */
export const TRANSFER_EXECUTE_V3_PREFIX = "/v3/transfer";

/** @type {"/requestCredin"} */
export const REQUEST_CREDIN_LEGACY_PATH = "/requestCredin";

/** @type {"/v3/credin"} */
export const REQUEST_CREDIN_V3_PATH = "/v3/credin";

/** @type {"/requestCredin/generateCredin"} */
export const REQUEST_CREDIN_GENERATE_LEGACY_PATH = "/requestCredin/generateCredin";

/** @type {"/v3/credin/confirmation"} */
export const REQUEST_CREDIN_GENERATE_V3_PATH = "/v3/credin/confirmation";

/** @type {"/confirmCredin/transaction"} */
export const CREDIN_TICKET_LEGACY_PATH = "/confirmCredin/transaction";

/** @type {"/v3/credin/ticket"} */
export const CREDIN_TICKET_V3_PATH = "/v3/credin/ticket";

/** @deprecated Use {@link REQUEST_CREDIN_V3_PATH}. Kept for redirects from old bookmarks. */
export const REQUEST_CREDIN_V3_PATH_LEGACY = "/v3/requestCredin";

/** @deprecated Use {@link REQUEST_CREDIN_GENERATE_V3_PATH}. Kept for redirects from old bookmarks. */
export const REQUEST_CREDIN_GENERATE_V3_PATH_LEGACY = "/v3/requestCredin/generateCredin";

const TRANSFER_EXECUTE_TYPES = ["internal", "thirdParties", "toContact"];

/**
 * Confirmación (revisión + token) previa al envío.
 *
 * El nombre legacy está invertido respecto de lo que hace cada pantalla: `/transfer/summary` es la
 * confirmación y `/transfer/confirmation` es el comprobante de éxito. Las rutas v3 no arrastran esa
 * inversión: `confirmation` confirma y `ticket` es el comprobante.
 *
 * @type {"/transfer/summary"}
 */
export const TRANSFER_CONFIRMATION_LEGACY_PATH = "/transfer/summary";

/** @type {"/v3/transfer/confirmation"} */
export const TRANSFER_CONFIRMATION_V3_PATH = "/v3/transfer/confirmation";

/** @type {"/v3/transfer/ticket"} */
export const TRANSFER_TICKET_V3_PATH = "/v3/transfer/ticket";

/**
 * Legacy abre una ruta distinta por estado de la transacción. En v3 hay una sola ruta de ticket y el
 * estado se resuelve desde Redux, así que este mapa sólo se usa para el fallback legacy.
 */
const TRANSFER_TICKET_LEGACY_PATH_BY_STATUS = {
    FINISHED: "/transfer/confirmation",
    PENDING: "/transfer/pending",
    PENDING_TOKEN: "/transfer/pending",
    FAILED: "/transfer/failed",
    CANCELLED: "/transfer/cancelled",
    SCHEDULED: "/transfer/scheduled",
};

const TRANSFER_TICKET_LEGACY_DEFAULT_PATH = "/transfer/confirmation";

/**
 * Post-login home route when session and environment are already resolved.
 * Must not return `/v3/dashboard` when `uxDashboardImprovements` is false, or
 * {@link V3PrivateDashboardRoute} would redirect-loop on `/v3/dashboard`.
 *
 * @param {boolean} uxDashboardImprovements - `true` → `/v3/dashboard`; `false` → legacy `/desktop`.
 * @returns {string}
 */
export function getPostLoginHomePath(uxDashboardImprovements) {
    return uxDashboardImprovements ? "/v3/dashboard" : "/desktop";
}

/**
 * Entry path for login when there is no session (deep links, logout, steps that require re-login).
 *
 * @param {boolean} uxLoginImprovements - When true, use v3 login at `/v3/login`; otherwise legacy root `/`.
 * @returns {string}
 */
export function getUnauthenticatedLoginPath(uxLoginImprovements) {
    return uxLoginImprovements ? "/v3/login" : "/";
}

/**
 * Environment selection (step 3) path for the active login shell.
 *
 * @param {boolean} uxLoginImprovements - When true, `/v3/login/step3`; otherwise legacy `/loginStep3`.
 * @returns {string}
 */
export function getLoginStep3Path(uxLoginImprovements) {
    return uxLoginImprovements ? "/v3/login/step3" : "/loginStep3";
}

/**
 * Terms acceptance (step 4) path for the active login shell.
 *
 * @param {boolean} uxLoginImprovements - When true, `/v3/login/step4`; otherwise legacy `/loginStep4`.
 * @returns {string}
 */
export function getLoginStep4Path(uxLoginImprovements) {
    return uxLoginImprovements ? "/v3/login/step4" : "/loginStep4";
}

/**
 * Ruta de lista "Mis cuentas" (menú lateral, rail v3, `goBack`, etc.): `/v3/accounts`, `/v2/accounts` o `/accounts`.
 *
 * - Con **Mis cuentas UX** y **Dashboard UX** activos → `/v3/accounts` (no se exigen favoritos Metrocorp/Funds).
 * - Si no aplica v3: sin favoritos Metrocorp/Funds → `/accounts` (legacy); con al menos uno → `/v2/accounts`.
 *
 * @returns {string}
 */
/**
 * Ruta de detalle de cuenta bancaria: v3 MVP2 o legacy.
 *
 * @param {string} accountId
 * @returns {string}
 */
export function getBankAccountDetailPath(accountId) {
    if (isUxMyAccountsImprovementsEnabled()) {
        return `/v3/accounts/${accountId}`;
    }
    return `/accounts/${accountId}`;
}

/**
 * Ruta v3 de saldos proyectados (requiere MVP2 Mis cuentas activo).
 *
 * @param {string} accountId
 * @returns {string}
 */
export function getBankAccountProjectedBalancePath(accountId) {
    return `/v3/accounts/${accountId}/projectedBalance`;
}

/**
 * Ruta v3 de saldos históricos (requiere MVP2 Mis cuentas activo).
 *
 * @param {string} accountId
 * @returns {string}
 */
export function getBankAccountHistoricBalancePath(accountId) {
    return `/v3/accounts/${accountId}/historicBalance`;
}

/**
 * Ruta del histórico de transferencias según flag UX Transferencias v3.
 *
 * @returns {typeof TRANSFER_HISTORIC_LEGACY_PATH | typeof TRANSFER_HISTORIC_V3_PATH}
 */
export function getTransferHistoricPath() {
    return isTransferHistoricV3Enabled() ? TRANSFER_HISTORIC_V3_PATH : TRANSFER_HISTORIC_LEGACY_PATH;
}

/**
 * Ruta de descarga del histórico de transferencias según flag UX Transferencias v3.
 *
 * @returns {typeof TRANSFER_DOWNLOAD_LEGACY_PATH | typeof TRANSFER_DOWNLOAD_V3_PATH}
 */
export function getTransferDownloadPath() {
    return isTransferHistoricV3Enabled() ? TRANSFER_DOWNLOAD_V3_PATH : TRANSFER_DOWNLOAD_LEGACY_PATH;
}

/**
 * Ruta de "Generar CREDIN" según flag UX Credin v3.
 *
 * @returns {typeof REQUEST_CREDIN_LEGACY_PATH | typeof REQUEST_CREDIN_V3_PATH}
 */
export function getRequestCredinPath() {
    return isCredinV3Enabled() ? REQUEST_CREDIN_V3_PATH : REQUEST_CREDIN_LEGACY_PATH;
}

/**
 * Ruta de confirmación/resumen CREDIN según flag UX Credin v3.
 *
 * @returns {typeof REQUEST_CREDIN_GENERATE_LEGACY_PATH | typeof REQUEST_CREDIN_GENERATE_V3_PATH}
 */
export function getGenerateCredinPath() {
    return isCredinV3Enabled() ? REQUEST_CREDIN_GENERATE_V3_PATH : REQUEST_CREDIN_GENERATE_LEGACY_PATH;
}

/**
 * CREDIN ticket (receipt) path according to the Credin v3 UX flag.
 *
 * @returns {typeof CREDIN_TICKET_LEGACY_PATH | typeof CREDIN_TICKET_V3_PATH}
 */
export function getCredinTicketPath() {
    return isCredinV3Enabled() ? CREDIN_TICKET_V3_PATH : CREDIN_TICKET_LEGACY_PATH;
}

/**
 * @param {string|undefined|null} pathname
 * @returns {boolean} true for the CREDIN ticket screen (v3 or legacy)
 */
export function isCredinTicketPath(pathname) {
    if (!pathname || typeof pathname !== "string") {
        return false;
    }
    return pathname === CREDIN_TICKET_LEGACY_PATH || pathname === CREDIN_TICKET_V3_PATH;
}

/**
 * True when the v3 shell back button should restore the CREDIN form (same as desktop `handleBack`).
 *
 * @param {string|undefined|null} pathname current location
 * @param {string|undefined|null} goBackPath shell `layout.goBackPath`
 * @returns {boolean}
 */
export function isCredinConfirmationBackToForm(pathname, goBackPath) {
    return pathname === REQUEST_CREDIN_GENERATE_V3_PATH && goBackPath === REQUEST_CREDIN_V3_PATH;
}

/**
 * @param {string|undefined|null} pathname
 * @returns {boolean} true para la pantalla de alta CREDIN (v3 o legacy)
 */
export function isRequestCredinPath(pathname) {
    if (!pathname || typeof pathname !== "string") {
        return false;
    }
    return (
        pathname === REQUEST_CREDIN_LEGACY_PATH ||
        pathname === REQUEST_CREDIN_V3_PATH ||
        pathname === REQUEST_CREDIN_V3_PATH_LEGACY
    );
}

/**
 * @param {string|undefined|null} pathname
 * @returns {boolean} true para la pantalla de confirmación CREDIN (v3 o legacy)
 */
export function isCredinConfirmationPath(pathname) {
    if (!pathname || typeof pathname !== "string") {
        return false;
    }
    return (
        pathname === REQUEST_CREDIN_GENERATE_LEGACY_PATH ||
        pathname === REQUEST_CREDIN_GENERATE_V3_PATH ||
        pathname === REQUEST_CREDIN_GENERATE_V3_PATH_LEGACY
    );
}

/**
 * @param {string|undefined|null} pathname
 * @returns {boolean}
 */
export function isTransferHistoricPath(pathname) {
    if (!pathname || typeof pathname !== "string") {
        return false;
    }
    return pathname === TRANSFER_HISTORIC_LEGACY_PATH || pathname === TRANSFER_HISTORIC_V3_PATH;
}

/**
 * Ruta de cursar transferencia según flag UX Transferencias v3.
 *
 * @param {"internal"|"thirdParties"|"toContact"} [transferType]
 * @param {{ suffix?: "modify"|"back" }} [options]
 * @returns {string}
 */
export function getTransferExecutePath(transferType = "internal", options = {}) {
    const type = TRANSFER_EXECUTE_TYPES.includes(transferType) ? transferType : "internal";
    const prefix = isTransferHistoricV3Enabled() ? TRANSFER_EXECUTE_V3_PREFIX : TRANSFER_EXECUTE_V2_PREFIX;
    const suffix = options.suffix ? `/${options.suffix}` : "";
    return `${prefix}/${type}${suffix}`;
}

/**
 * @param {string|undefined|null} pathname
 * @returns {boolean}
 */
export function isTransferExecutePath(pathname) {
    if (!pathname || typeof pathname !== "string") {
        return false;
    }
    // `/v3/transfer/confirmation` y `/v3/transfer/ticket` comparten prefijo con cursar transferencia
    // pero no son pantallas de alta.
    if (pathname === TRANSFER_CONFIRMATION_V3_PATH || pathname === TRANSFER_TICKET_V3_PATH) {
        return false;
    }
    return (
        pathname.startsWith(`${TRANSFER_EXECUTE_V2_PREFIX}/`) || pathname.startsWith(`${TRANSFER_EXECUTE_V3_PREFIX}/`)
    );
}

/**
 * Extrae el tipo de transferencia desde la URL de execute.
 *
 * @param {string|undefined|null} pathname
 * @returns {"internal"|"thirdParties"|"toContact"|null}
 */
export function getTransferExecuteTypeFromPath(pathname) {
    if (!pathname || typeof pathname !== "string") {
        return null;
    }
    const match = pathname.match(/^\/v[23]\/transfer\/([^/]+)/);
    const segment = match?.[1];
    return TRANSFER_EXECUTE_TYPES.includes(segment) ? segment : null;
}

/**
 * Ruta de confirmación de transferencia según flag UX Transferencias v3.
 *
 * @returns {typeof TRANSFER_CONFIRMATION_LEGACY_PATH | typeof TRANSFER_CONFIRMATION_V3_PATH}
 */
export function getTransferConfirmationPath() {
    return isTransferHistoricV3Enabled() ? TRANSFER_CONFIRMATION_V3_PATH : TRANSFER_CONFIRMATION_LEGACY_PATH;
}

/**
 * Ruta del comprobante de transferencia. En v3 es única para todos los estados; en legacy hay una por estado.
 *
 * @param {string|undefined|null} [idTransactionStatus]
 * @returns {string}
 */
export function getTransferTicketPath(idTransactionStatus) {
    if (isTransferHistoricV3Enabled()) {
        return TRANSFER_TICKET_V3_PATH;
    }
    return TRANSFER_TICKET_LEGACY_PATH_BY_STATUS[idTransactionStatus] || TRANSFER_TICKET_LEGACY_DEFAULT_PATH;
}

/**
 * @param {string|undefined|null} pathname
 * @returns {boolean}
 */
export function isTransferConfirmationPath(pathname) {
    if (!pathname || typeof pathname !== "string") {
        return false;
    }
    return pathname === TRANSFER_CONFIRMATION_LEGACY_PATH || pathname === TRANSFER_CONFIRMATION_V3_PATH;
}

/**
 * @param {string|undefined|null} pathname
 * @returns {boolean}
 */
export function isTransferTicketPath(pathname) {
    if (!pathname || typeof pathname !== "string") {
        return false;
    }
    if (pathname === TRANSFER_TICKET_V3_PATH) {
        return true;
    }
    return Object.values(TRANSFER_TICKET_LEGACY_PATH_BY_STATUS).includes(pathname);
}

export function getV3PrivateNavAccountsPath() {
    if (isUxMyAccountsImprovementsEnabled() && isUxDashboardImprovementsEnabled()) {
        return "/v3/accounts";
    }

    const confFavoriteAccountMetrocorp = config.getBoolean("frontend.favoriteAccount.Metrocorp.functionalities", false);
    const confFavoriteAccountFundcorp = config.getBoolean("frontend.favoriteAccount.Funds.functionalities", false);
    const showAccountsV2 = confFavoriteAccountMetrocorp || confFavoriteAccountFundcorp;
    if (!showAccountsV2) {
        return "/accounts";
    }
    return "/v2/accounts";
}
